import { AlertTriangle, UserX } from 'lucide-react';
import { useState } from 'react';

import { revokeInvite as revokeInviteApi, UserMgmtError } from '../../../../api/users';
import { DialogFooterActions } from '../../../../shared/DialogFooterActions';
import type { UserRow } from '../userViewModel';

import ModalScrim from './ModalScrim';

interface RevokeInviteModalProps {
  user: UserRow;
  onClose: () => void;
  /** Called after the revoke succeeds. */
  onRevoked: () => void;
}

const ERROR_TEXT: Record<string, string> = {
  NOT_INVITED: 'This invitation was already accepted or revoked. Refresh the list.',
  TARGET_NOT_FOUND: 'User not found. They may have been removed in another session.',
  RD_TARGET_OUT_OF_SCOPE: 'This user has regions outside your scope. Ask an Admin to revoke.',
  TARGET_OUTRANKS_CALLER: 'Only an Admin can change this user.',
};

function explainError(err: unknown): string {
  if (!(err instanceof UserMgmtError)) {
    return err instanceof Error ? err.message : 'Revoke failed. Please retry.';
  }
  return (err.code && ERROR_TEXT[err.code]) || err.message;
}

/**
 * Confirmation dialog for revoking a pending invite.
 */
export default function RevokeInviteModal({ user, onClose, onRevoked }: RevokeInviteModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await revokeInviteApi(user.id);
      onRevoked();
    } catch (err) {
      setError(explainError(err));
      setSubmitting(false);
    }
  }

  return (
    <ModalScrim width={520} onClose={onClose} labelledBy="revoke-invite-title">
      <div className="px-[22px] pt-5 pb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-clay-50 dark:bg-clay-900/20 text-clay-700 dark:text-clay-300 flex items-center justify-center flex-none">
          <UserX size={20} />
        </div>
        <div className="flex-1">
          <h2
            id="revoke-invite-title"
            className="m-0 text-xl font-bold text-bark-900 dark:text-[#e8ece9] leading-none"
          >
            Revoke invite?
          </h2>
          <div className="text-[12.5px] text-stone-600 dark:text-stone-400 mt-1.5 leading-snug">
            <strong className="text-bark-900 dark:text-[#e8ece9]">{user.name}</strong>
            {user.email ? ` (${user.email})` : ''} will no longer be able to accept this invitation.
            Their pending sign-in account and regions are removed and they return to Imported. You
            can invite them again later.
          </div>
        </div>
      </div>
      {error && (
        <div
          role="alert"
          className="mx-[22px] mb-3 flex gap-2 items-start text-[12px] rounded-lg px-3 py-2.5 border bg-clay-50 border-clay-200 text-clay-800 dark:bg-clay-900/20 dark:border-clay-800 dark:text-clay-300"
        >
          <AlertTriangle size={14} className="flex-none mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      <DialogFooterActions
        note="Audit event will record who revoked it."
        onCancel={onClose}
        confirmLabel="Revoke invite"
        confirmBusyLabel="Revoking…"
        confirmType="button"
        onConfirm={handleConfirm}
        busy={submitting}
      />
    </ModalScrim>
  );
}
